<template>
  <section class="store-seller-form">
    <template v-if="showHeader">
      <PageBack :to="listRoute" />
      <PageHeader :title="title" />
    </template>

    <form v-if="!edit" @submit.prevent="submit">
      <FormMessage v-if="formError" :error="true">{{ formError }}</FormMessage>

      <template v-for="field in visibleFields">
        <Field
          :key="field.name"
          :model="formData[field.name]"
          :name="field.name"
          :label="fieldLabel(field)"
          :type="field.type || 'text'"
          :required="field.required"
          @change="onFieldChange(field.name, $event)"
        />
        <FormMessage
          v-if="fieldErrors[field.name]"
          :key="`err-${field.name}`"
          :error="true"
          class="store-seller-form__field-error"
        >
          {{ fieldErrors[field.name] }}
        </FormMessage>
      </template>

      <FieldSelect
        v-if="storeOptions.length"
        :selected="formData.realStoreIds"
        :label="`${$t('global.stores')} *`"
        :options="storeOptions"
        :multiple="true"
        @input="onStoresChange"
      />
      <FormMessage v-if="fieldErrors.realStoreIds" :error="true" class="store-seller-form__field-error">
        {{ fieldErrors.realStoreIds }}
      </FormMessage>

      <ButtonForm
        :disabled="isSubmitDisabled"
        :loading="sellerLoading"
        >{{ $t('global.validate') }}</ButtonForm
      >
    </form>

    <div v-else-if="model" class="store-seller-form__details">
      <p
        v-for="field in editDetailFields"
        :key="field"
        class="store-seller-form__detail"
      >
        <strong>{{ $t(`global.${field}`) }} :</strong>
        {{ model[field] }}
      </p>
      <p v-if="model.stores?.length" class="store-seller-form__detail">
        <strong>{{ $t('global.stores') }} :</strong>
        {{ model.stores.map((s) => s.label).join(', ') }}
      </p>
    </div>

    <PopinNotification ref="popinSuccess" :text="successMsg" />
    <PopinNotification ref="popinError" :text="errorMsg" />
  </section>
</template>

<script>
import PageBack from '@/components/ui/page/PageBack';
import PageHeader from '@/components/ui/page/PageHeader';
import Field from '@/components/ui/form/fields/Field';
import FieldSelect from '@/components/ui/form/fields/FieldSelect';
import ButtonForm from '@/components/ui/button/ButtonForm';
import PopinNotification from '@/components/ui/popin/PopinNotification';
import FormMessage from '@/components/ui/form/FormMessage';
import { translateApiErrorMessage } from '@/utils';
import { clientIdFromRoute, storeSellerListRoute } from '@/utils/store-seller-routes';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'StoreSellerForm',
  components: {
    FormMessage,
    PopinNotification,
    ButtonForm,
    FieldSelect,
    Field,
    PageBack,
    PageHeader,
  },
  props: {
    title: String,
    action: Function,
    successMsg: String,
    errorMsg: String,
    model: Object,
    edit: Boolean,
    showHeader: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      editDetailFields: ['firstname', 'lastname', 'email'],
      formData: {
        email: null,
        password: null,
        firstname: null,
        lastname: null,
        realStoreIds: [],
      },
      fields: [
        { name: 'firstname', required: true },
        { name: 'lastname', required: true },
        { name: 'email', type: 'email', required: true },
        { name: 'password', type: 'password', required: true },
      ],
      fieldErrors: {},
      formError: null,
    };
  },
  computed: {
    ...mapState(['sellerLoading', 'user', 'temporaryUser', 'clientStores']),
    clientId() {
      return clientIdFromRoute(this.$route);
    },
    managerStores() {
      const user = this.temporaryUser || this.user;
      return user?.stores || [];
    },
    isStoreScopedUser() {
      const roles = (this.temporaryUser || this.user)?.roles || [];
      if (roles.includes('ROLE_BRAND_ADMIN')) {
        return false;
      }
      return roles.includes('ROLE_STORE_MANAGER') || roles.includes('ROLE_STORE_SELLER');
    },
    storeOptions() {
      let stores = this.clientId ? this.clientStores : this.managerStores;
      if (this.clientId && this.isStoreScopedUser && this.managerStores.length) {
        stores = stores.filter((store) =>
          this.managerStores.some((userStore) => userStore.id === store.id)
        );
      }
      return stores.map((s) => ({
        id: s.id,
        label: s.name || s.label,
      }));
    },
    visibleFields() {
      return this.fields;
    },
    listRoute() {
      return storeSellerListRoute(this.$route.params.id);
    },
    isSubmitDisabled() {
      if (
        !this.formData.email ||
        !this.formData.firstname ||
        !this.formData.lastname
      ) {
        return true;
      }
      if (!this.edit && !this.formData.password) return true;
      if (!this.formData.realStoreIds?.length && this.storeOptions.length) {
        return true;
      }
      return false;
    },
  },
  watch: {
    clientId: {
      immediate: true,
      handler(clientId) {
        if (clientId) {
          this.fetchClientStores(clientId).then(() => this.autoSelectSingleStore());
        }
      },
    },
  },
  methods: {
    ...mapActions(['fetchClientStores']),
    autoSelectSingleStore() {
      if (this.model || this.formData.realStoreIds?.length) return;
      if (this.storeOptions.length === 1) {
        this.formData.realStoreIds = [this.storeOptions[0].id];
      }
    },
    fieldLabel(field) {
      const title = this.$t(`global.${field.name}`);
      return field.required ? `${title} *` : title;
    },
    clearFieldError(name) {
      if (!this.fieldErrors[name]) return;
      const next = { ...this.fieldErrors };
      delete next[name];
      this.fieldErrors = next;
    },
    onFieldChange(name, value) {
      this.formData[name] = value;
      this.clearFieldError(name);
      this.formError = null;
    },
    onStoresChange(ids) {
      this.formData.realStoreIds = ids;
      this.clearFieldError('realStoreIds');
      this.formError = null;
    },
    applyValidationError(error) {
      const next = {};
      if (error?.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([path, msg]) => {
          next[path] = translateApiErrorMessage(this.$i18n, msg);
        });
      }
      this.fieldErrors = next;
      if (error?.message) {
        this.formError = translateApiErrorMessage(this.$i18n, error.message);
      } else if (!Object.keys(next).length) {
        this.formError = this.errorMsg || this.$t('global.default_request_error');
      } else {
        this.formError = null;
      }
    },
    async submit() {
      if (!this.action) return;
      this.fieldErrors = {};
      this.formError = null;
      const payload = { ...this.formData };
      try {
        const seller = await this.action(payload);
        this.$refs.popinSuccess.open();
        this.$emit('next', seller);
      } catch (error) {
        if (error?.status === 422 || error?.fieldErrors || error?.message) {
          this.applyValidationError(error);
        } else {
          this.$refs.popinError.open();
        }
      }
    },
  },
  mounted() {
    if (this.model) {
      this.formData.email = this.model.email;
      this.formData.firstname = this.model.firstname;
      this.formData.lastname = this.model.lastname;
      this.formData.realStoreIds = (this.model.stores || []).map((s) => s.id);
    } else {
      this.autoSelectSingleStore();
    }
  },
};
</script>

<style lang="scss">
.store-seller-form__field-error {
  margin-top: -10px;
  margin-bottom: 10px;
}

.store-seller-form__details {
  margin-bottom: 24px;
}

.store-seller-form__detail {
  margin: 0 0 12px;

  &:last-child {
    margin-bottom: 0;
  }
}
</style>
